import { addMinutes, format } from 'date-fns';
import { useCallback, useMemo } from 'react';

import StoreService from '@lib/api/StoreService';
import {
    type DeliveryType,
    type Store,
    datesToTimeSlots,
    getClose,
    getTimeSlotDate,
    isClosed
} from '@lib/entities/Store';
import { type ApplicationState, useApplicationState } from '@lib/state';

const selectSchedule = (s: ApplicationState) => s.schedule;

type HubState = 'not-hub' | 'hub-closed' | 'hub-open';

interface FetchTimeSlotsParams {
    store: Store;
    days?: number;
    deliveryType: DeliveryType;
    cartItemCount?: number;
}
const DeliveryTimeRange = 20; // minutes
export const useSchedule = () => {
    const state = useApplicationState(selectSchedule);
    const {
        timeSlots,
        setStoreAndTimeSlots,
        beginFetch,
        currentDay,
        currentTime,
        store: currentStore
    } = state;

    const fetchTimeSlots = useCallback(
        async ({ store, deliveryType, days = 2, cartItemCount }: FetchTimeSlotsParams) => {
            // Claim a fetch token before awaiting the network. If a newer fetch
            // (e.g. visitStore selecting a different store) starts while this is in
            // flight, this result is dropped instead of clobbering `schedule.store`
            // — preventing the stale-store quote ("Store Unavailable For Delivery").
            const seq = beginFetch();

            const times =
                deliveryType === 'Delivery'
                    ? await StoreService.fetchDeliveryTimes(store.id)
                    : await StoreService.fetchPickupTimes(store.id, cartItemCount);

            const timeSlots = datesToTimeSlots(times).slice(0, days);

            setStoreAndTimeSlots({
                store,
                timeSlots,
                range: deliveryType === 'Delivery' ? DeliveryTimeRange : undefined,
                seq
            });

            return times;
        },
        [beginFetch, setStoreAndTimeSlots]
    );

    const isScheduled = useMemo(() => {
        if (!currentStore) {
            return false;
        }

        if (isClosed(currentStore)) {
            return true;
        }

        return currentDay !== 0 || currentTime !== 0;
    }, [currentStore, currentDay, currentTime]);

    const selectedTimeSlot = useMemo(() => {
        return getTimeSlotDate(timeSlots, currentDay, currentTime);
    }, [timeSlots, currentDay, currentTime]);

    const days = useMemo(() => timeSlots.map(([day]) => day), [timeSlots]);
    const times = useMemo(() => timeSlots[currentDay]?.[1] ?? [], [timeSlots, currentDay]);

    const defaultTimeSlot = useMemo(() => {
        return addMinutes(new Date(), 20);
    }, [timeSlots]);

    const apiFormattedTimeSlot = useMemo(() => {
        const time = selectedTimeSlot ?? defaultTimeSlot;
        return format(time, 'yyyy-MM-dd HH:mm');
    }, [selectedTimeSlot, defaultTimeSlot]);

    const hubOpenMessage = useMemo(() => {
        if (!selectedTimeSlot) {
            return undefined;
        }

        if (!currentStore) {
            return undefined;
        }

        if (!currentStore.isHub) {
            return undefined;
        }

        const pickup = selectedTimeSlot.toLocaleTimeString(['en-US'], {
            hour: '2-digit',
            minute: '2-digit'
        });

        const closing = getClose(currentStore, new Date())?.toLocaleTimeString(['en-US'], {
            hour: '2-digit',
            minute: '2-digit'
        });

        return (
            'You are now ordering from a Just Salad Hub location. ' +
            `Place your order before ${closing} and receive it by ${pickup}.`
        );
    }, [selectedTimeSlot, currentStore]);

    const hubState = useMemo<HubState>(() => {
        if (!currentStore?.isHub) {
            return 'not-hub';
        }

        if (!selectedTimeSlot) {
            return 'hub-closed';
        }

        return 'hub-open';
    }, [selectedTimeSlot, currentStore]);

    return {
        ...state,
        fetchTimeSlots,
        selectedTimeSlot,
        defaultTimeSlot,
        days,
        times,
        apiFormattedTimeSlot,
        isScheduled,
        hubState,
        hubOpenMessage
    };
};

export default useSchedule;
